import type { FileRecord, FileRecordClass } from "@cohesive/api-client";

/**
 * Per-record payload for cross-scope drags. Carries enough source
 * context that the dispatcher can run `classifyTransfer` purely
 * from the data attached to `useDraggable` — no DOM lookups, no
 * cross-component coordination.
 */
export interface ExplorerFileRecordCopyPayload {
  recordId: string;
  sourceClass: FileRecordClass;
  sourceCanvasId?: string;
  sourceWorkspaceId?: string;
  sourceFlowSessionId?: string;
  /** Full source path (e.g. "Reports/q1.pdf") — leaf used for collision UX. */
  sourcePath: string;
  /** Whether the source record is a folder marker — relevant for cascades. */
  isFolder: boolean;
  /**
   * The drag originates from the "Shared with me" rail — i.e. a file the user
   * reaches via a direct share, not via the source workspace. Dropping it into a
   * workspace must **copy** (the user can't relocate a file out of the owner's
   * scope), never move. Set only by the shared-rail rows.
   */
  sharedSource?: boolean;
  contentType: string;
  /** Display name (path leaf) — used by the receiving toast / drag preview. */
  name: string;
  /** Natural pixel dimensions of image/video records — forwarded from the
   *  source FileRecord so the canvas can size the loading node correctly
   *  before the copy API returns. */
  width?: number;
  height?: number;
  /**
   * Canvas-source rows only: whether the file currently has a node on
   * the source canvas. Drives the "stored vs placed" disambiguation —
   * drops onto the same canvas are no-ops only when every payload is
   * already placed; an unplaced (stored) row dropped onto its own
   * canvas should mint a node for the existing record.
   *
   * Undefined preserves legacy behavior (treated as placed). Workspace
   * sources don't set this.
   */
  sourcePlaced?: boolean;
  /**
   * Canvas-source rows only: the section id the placed node currently
   * lives in, or `null` for canvas root. Used by the classifier to
   * decide whether dropping a placed file onto a section / root is an
   * actual reparent or a no-op. Undefined preserves legacy behavior.
   */
  sourceParentSectionId?: string | null;
}

/**
 * Identifies the source scope of a record being dragged from the
 * explorer. The wire `FileRecord` carries `class` but not `canvas_id` /
 * `workspace_id` / `flow_session_id` — those live on the listing query
 * the row came from, so the drag handler passes them in explicitly.
 */
export interface ExplorerCopySourceScope {
  canvasId?: string;
  workspaceId?: string;
  flowSessionId?: string;
}

/**
 * Drag payload for a Canvas section (from the sidebar's section view).
 * Carries only the section's direct file_record children — sub-sections,
 * shapes, text, entities, widgets, and grand-children inside any nested
 * section are deliberately excluded. The destination side (workspace
 * folder) creates a folder named after the section and copies these
 * files into it; non-file canvas content has no meaningful workspace
 * representation.
 *
 * The drag is treated as a no-op when `files` is empty.
 */
export interface ExplorerSectionPayload {
  sectionId: string;
  sectionName: string;
  sourceCanvasId: string;
  files: ExplorerFileRecordCopyPayload[];
}

/**
 * Source scope for a drag, derived from the record's own class + scope ids.
 * Files-rail rows span classes now (workspace files, plus directly-shared
 * canvas / flow-session files in "Shared with me"), so routing must follow the
 * record rather than assume workspace.
 */
export function copySourceScopeFromRecord(
  record: FileRecord,
  fallbackWorkspaceId?: string,
): ExplorerCopySourceScope {
  switch (record.class) {
    case "canvas":
      return { canvasId: record.canvas_id };
    case "flow-session":
      return { flowSessionId: record.flow_session_id };
    default:
      return { workspaceId: record.workspace_id ?? fallbackWorkspaceId };
  }
}

export function buildCopyPayloadFromRecord(
  record: FileRecord,
  scope: ExplorerCopySourceScope,
  opts?: { placed?: boolean; parentSectionId?: string | null },
): ExplorerFileRecordCopyPayload {
  const segments = record.path.split("/").filter(Boolean);
  const isFolder = record.path.endsWith("/");
  const name = segments[segments.length - 1] || record.id;
  return {
    recordId: record.id,
    sourceClass: record.class,
    sourceCanvasId: scope.canvasId,
    sourceWorkspaceId: scope.workspaceId,
    sourceFlowSessionId: scope.flowSessionId,
    sourcePath: record.path,
    isFolder,
    contentType: record.content_type,
    name,
    ...(record.width != null ? { width: record.width } : {}),
    ...(record.height != null ? { height: record.height } : {}),
    ...(opts?.placed !== undefined ? { sourcePlaced: opts.placed } : {}),
    ...(opts?.parentSectionId !== undefined
      ? { sourceParentSectionId: opts.parentSectionId }
      : {}),
  };
}
